import React from 'react';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Plus, Pencil, Trash2, Star, Users, MapPin, Mail, Phone, Globe, Award, Check } from 'lucide-react';
import { getTypeLabel } from './professionalConstants';
import ProfessionalForm from './ProfessionalForm';

export default function ProfessionalsTab({
  professionals,
  showForm,
  formData,
  setFormData,
  editingItem,
  expandedProf,
  setExpandedProf,
  includeInactive,
  setIncludeInactive,
  onAddNew,
  onEdit,
  onSubmit,
  onCancel,
  onDeactivate,
  onActivate,
}) {
  if (showForm) {
    return (
      <ProfessionalForm
        formData={formData}
        setFormData={setFormData}
        isEditing={!!editingItem}
        onSubmit={onSubmit}
        onCancel={onCancel}
      />
    );
  }

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-4">
          <Button
            onClick={onAddNew}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="add-professional-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Professional
          </Button>
          <label className="flex items-center gap-2 text-sm text-slate-400">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="rounded"
            />
            Show Inactive
          </label>
        </div>
      </div>

      <div className="grid gap-4">
        {professionals.map(prof => (
          <Card key={prof.professional_id} className={`bg-slate-800 border-slate-700 ${!prof.is_active ? 'opacity-60' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-white">{prof.name}</h3>
                    {prof.is_featured && (
                      <Badge className="bg-amber-600 text-white">
                        <Star className="h-3 w-3 mr-1" />
                        Featured
                      </Badge>
                    )}
                    {!prof.is_active && (
                      <Badge variant="secondary" className="bg-red-600/20 text-red-400">
                        Inactive
                      </Badge>
                    )}
                  </div>
                  <p className="text-amber-400 text-sm">{getTypeLabel(prof.professional_type)}</p>
                  {prof.company && <p className="text-slate-400 text-sm">{prof.company}</p>}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {prof.location && (
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> {prof.location}
                      </span>
                    )}
                    {prof.city && !prof.location && (
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> {prof.city}{prof.state ? `, ${prof.state}` : ''}
                      </span>
                    )}
                    {prof.email && (
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Mail className="h-3 w-3" /> {prof.email}
                      </span>
                    )}
                    {prof.phone && (
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Phone className="h-3 w-3" /> {prof.phone}
                      </span>
                    )}
                    {prof.website && (
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Globe className="h-3 w-3" /> {prof.website}
                      </span>
                    )}
                    {prof.member_discount && (
                      <Badge variant="outline" className="text-green-400 border-green-400/30 text-xs">
                        <Award className="h-3 w-3 mr-1" />
                        {prof.member_discount}
                      </Badge>
                    )}
                    {prof.founding_advisor && (
                      <Badge className="bg-amber-600 text-white text-xs">
                        <Award className="h-3 w-3 mr-1" />
                        Founding Advisor
                      </Badge>
                    )}
                  </div>
                  {prof.bio && (
                    <div className="mt-2">
                      <p className={`text-slate-400 text-sm ${expandedProf === prof.professional_id ? '' : 'line-clamp-2'}`}>{prof.bio}</p>
                      {prof.bio.length > 120 && (
                        <button
                          onClick={() => setExpandedProf(expandedProf === prof.professional_id ? null : prof.professional_id)}
                          className="text-amber-400 text-xs mt-1 hover:underline"
                          data-testid={`toggle-bio-${prof.professional_id}`}
                        >
                          {expandedProf === prof.professional_id ? 'Show less' : 'View full bio'}
                        </button>
                      )}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(prof.specialties || []).slice(0, 3).map((spec, i) => (
                      <Badge key={i} variant="secondary" className="bg-slate-700 text-slate-300 text-xs">
                        {spec}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(prof)}
                    className="text-slate-400 hover:text-white"
                    data-testid={`edit-professional-${prof.professional_id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {prof.is_active ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeactivate(prof)}
                      className="text-red-400 hover:text-red-300"
                      data-testid={`deactivate-professional-${prof.professional_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onActivate(prof)}
                      className="text-green-400 hover:text-green-300"
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {professionals.length === 0 && (
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-8 text-center">
              <Users className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No professionals yet. Add your first one!</p>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
}
